/**
 * modalNavigatorStore
 *
 * A redux store which orchstrates navigation between modals and opening up
 * modals from within the current modal, e.g. related videos from exercises.
 */
import {
    applyMiddleware,
    createStore,
    type Dispatch,
    type MiddlewareAPI,
    type Store,
} from "redux";

// NOTE(jvoll): OK to import BigBingoInternals from here. This is one place
// where we want to mark an LTv2 conversion but cannot use a ConversionContext
// (since we are marking the conversion from redux, not a react component).
import {
    enums,
    type LearningTimeContent,
    type PartialLearningTimeInfo,
} from "@khan/analytics-learning-time";
// eslint-disable-next-line no-restricted-imports
import * as BigBingoInternals from "@khan/analytics-learning-time/bigbingo-internals";
import {
    createTestPrepStruct,
    mapContentKindToActivity,
    mapQuizToContentKind,
    maybeGetTestPrepSubject,
} from "@khan/analytics-learning-time/utils";
import {createCompose} from "@khan/util-redux-create-compose";

import {getSourceId} from "../utils/quiz-utils.ts";

import reducer from "./modal-navigator-reducer.ts";
import type {
    Action,
    ModalData,
    ModalItem,
    State,
    State as StoreState,
} from "./modal-navigator-types.ts";

/**
 * Construct the 'extra' param for logging a modal-related conversion.
 */
export const getModalItemExtra = (
    item: ModalItem,
    referrer: string,
): {
    content_id: string;
    is_recommended: null | boolean;
    kind:
        | "Article"
        | "Challenge"
        | "Exercise"
        | "Interactive"
        | "InterspersedQuiz"
        | "Project"
        | "Scratchpad"
        | "Talkthrough"
        | "Video";
    referrer: string;
    slug: string;
} => {
    return {
        content_id:
            item.kind === "InterspersedQuiz"
                ? getSourceId(item)
                : item.contentId,
        is_recommended:
            item.kind === "InterspersedQuiz" ? null : item.recommended,
        kind: item.kind,
        slug: item.slug,
        referrer,
    };
};

/**
 * Mark a content_modal_view conversion.
 */
const markItemView = (modalData: ModalData) => {
    const {currentItem, referrer} = modalData;
    // TODO(jvoll): Consider making this a minor conversion so that it's data
    // is better populated. Right now the learning time population is not
    // great for these conversions but there is (almost?) always a
    // well filled-in `conent_model_open` or `content_modal_view` fired before
    // this that we could inherit LearingTimeInfo from. No, that's not a typo,
    // we seem to fire two `content_modal_view` conversions a lot of times.
    // This duplication was originally done for LTv2 purposes, (see:
    // https://github.com/Khan/webapp/commit/dafcefde0cfc36570c40dabc26a95d10f303e36d)
    // but, it seems like we could try to get stop firing this conversion from
    // this redux middleware all together, or make it minor. One place I do
    // see only one of these firing is when we use the next/previous arrows,
    // in that case making it minor could mean that start-of-task cards on the
    // "next" ModalItem are not attributed correclty.
    // https://khanacademy.atlassian.net/browse/LP-6567
    BigBingoInternals.markConversion(
        "content_modal_view",
        getModalItemExtra(currentItem, referrer),
        modalItemLearningTimeInfo(currentItem),
    );
};

export const modalItemLearningTimeInfo = (
    item: ModalItem,
): PartialLearningTimeInfo => {
    // Course Challenges will not have contentIds.
    const kind = modalItemLearnableContentKind(item);

    // This logic was originally added for getting the content path (DCUL)
    // for modal items displayed within assignments on the
    // Logged-in-homepage (since the page itself has no DCUL context like
    // tutorial & library pages do).
    // Now, we also use it for populating the `lessonId` in the library since
    // the modal is not in the react hierarchy at a place where we have the
    // `lessonId` readily available. The modal is mounted within the
    // `CurationPage` component (and thus has D/C/U in it's ConversionContext)
    // but not within the `LessonCard` component (since a user can actually
    // navigate across multiple `LessonCard`s and into a Quiz). In order to
    // populate the `lessonId` on `nodePath`, we fill it in
    // manually when getting the ModalItems in modal-route.jsx.
    const {nodePath = {}} = item;
    const {domainId, courseId, unitId, lessonId} = nodePath;

    const content: Partial<LearningTimeContent> = {
        kind,
        contentTitle: item.title,
    };
    if (item.contentId) {
        content.contentId = item.contentId;
    }
    if (domainId) {
        content.domainId = domainId;
    }
    if (courseId) {
        content.courseId = courseId;
    }
    if (unitId) {
        content.unitId = unitId;
    }
    if (lessonId) {
        content.lessonId = lessonId;
    }

    let testPrep = null;
    // @ts-expect-error - TS2339 - Property 'nodeUrl' does not exist on type 'ModalItem'. | TS2339 - Property 'nodeUrl' does not exist on type 'ModalItem'.
    if (item.nodeUrl && typeof item.nodeUrl === "string") {
        // @ts-expect-error - TS2339 - Property 'nodeUrl' does not exist on type 'ModalItem'.
        const url = item.nodeUrl;
        const maybeTestPrepSubject = maybeGetTestPrepSubject(url);
        // @ts-expect-error - TS2322 - Type 'TestPrepStruct | null | undefined' is not assignable to type 'null'.
        testPrep = maybeTestPrepSubject
            ? createTestPrepStruct(maybeTestPrepSubject)
            : null;
    }

    return {
        product: enums.Product.OTHER_PRODUCT,
        activity: mapContentKindToActivity(kind),
        content,
        ...(testPrep ? {testPrep} : null),
    };
};

export const modalItemLearnableContentKind = (
    item: ModalItem,
): enums.LearningTimeContentKind => {
    // NOTE(slim): "Scratchpad" is a legacy scratchpad type. All new
    // scratchpads are either a Challenge, Project, Talkthrough, or Interactive.
    if (item.kind === "Scratchpad") {
        switch (item.category) {
            case "challenge":
                return enums.ContentKind.Challenge;
            case "project":
                return enums.ContentKind.Project;
            // @ts-expect-error - TS2678 - Type '"interactive"' is not comparable to type '"tutorial" | "official" | "documentation" | "sanctioned" | "challenge" | "project"'.
            case "interactive":
                return enums.ContentKind.Interactive;
            case "tutorial":
                // Confirmed with Pamela that "tutorial" maps to Talkthrough.
                return enums.ContentKind.Talkthrough;
            default:
                // NOTE(slim): In practice we expect to see very few of these.
                // This return value is chosen based on the overall
                // proportion of content, according to Pamela.
                return enums.ContentKind.Interactive;
        }
    }
    if (item.kind === "InterspersedQuiz") {
        return mapQuizToContentKind(item);
    }
    return item.kind;
};

/**
 * Redux Middleware used to mark a modal view conversion.
 */
const viewMarker =
    // @ts-expect-error - TS2707 - Generic type 'MiddlewareAPI<D, S>' requires between 0 and 2 type arguments.

    (store: MiddlewareAPI<State, Action, Dispatch<Action>>) =>
        (next: Dispatch<Action>) =>
        (action: Action) => {
            const result = next(action);
            if (
                action.type === "OpenItem" ||
                action.type === "NextItem" ||
                action.type === "PrevItem"
            ) {
                const {modalData} = store.getState() as StoreState;
                if (modalData) {
                    markItemView(modalData);
                }
            }
            return result;
        };

const compose = createCompose({
    reduxDevtoolsOptions: {
        name: "Modal Navigator",
    },
});

// @ts-expect-error - TS2707 - Generic type 'Store<S, A>' requires between 0 and 2 type arguments.
const store: Store<State, Action, Dispatch<Action>> = createStore<
    // @ts-expect-error - TS2558 - Expected 4 type arguments, but got 3.
    State,
    Action,
    Dispatch<Action>
>(reducer, compose(applyMiddleware(viewMarker)));

export default store;
